import { GetNewsAuthors_authors } from "src/api/generated/GetNewsAuthors";
import { GetNewsOverview_newsitems } from "src/api/generated/GetNewsOverview";

export interface INewsListRealestate {
    id: string;
    name: string;
    number: string;
}

export interface INewsListAuthor {
    name: string;
    email: string;
}

export interface INewsListItem {
    id: string;
    title: string;
    timestamp: string | null;
    // UTC without a time zone.
    updated: string;
    state: number;
    authorId?: string;
    // Unknown for authors that are no user of the customer, e.g. the ERP.
    author?: INewsListAuthor;
    // The realestates the user can see, by number.
    realestates: INewsListRealestate[];
    // All realestates of the news, including those without access.
    realestateCount: number;
}

// Like ums.get_user_name in the database: both names, or only one if they are the same.
export const getUserDisplayName = (name1: string, name2: string | null): string =>
    name1 === name2 ? name1 : `${name1} ${name2 ?? ""}`.trim();

export const getNewsListItems = (
    newsitems: GetNewsOverview_newsitems[],
    authors: GetNewsAuthors_authors[]
): INewsListItem[] => {
    const authorById = new Map(
        authors.map((author) => [
            author.id,
            { name: getUserDisplayName(author.name1, author.name2), email: author.email ?? "" }
        ])
    );

    return newsitems.map((newsitem) => ({
        id: newsitem.id,
        title: newsitem.news[0]?.title ?? "",
        timestamp: newsitem.timestamp,
        updated: newsitem.updated,
        state: newsitem.state,
        authorId: newsitem.author ?? undefined,
        author: authorById.get(newsitem.author),
        realestates: newsitem.newsrealestates.flatMap(({ realestate }) =>
            realestate ? [{ id: realestate.id, name: realestate.name, number: realestate.number }] : []
        ),
        realestateCount: newsitem.newsrealestates.length
    }));
};

// Searches the title and the name and number of the realestates.
export const matchesNewsSearchQuery = ({ title, realestates }: INewsListItem, query: string): boolean => {
    const normalizedQuery = query.trim().toLowerCase();
    if (normalizedQuery === "") return true;

    return [title, ...realestates.flatMap((realestate) => [realestate.name, realestate.number])].some((text) =>
        text.toLowerCase().includes(normalizedQuery)
    );
};

const DISPLAYED_REALESTATES_COUNT = 2;

// The list names the first two realestates and counts the others ("+ 3 weitere").
// Realestates the user cannot see are only counted.
export const getDisplayedRealestates = ({ realestates, realestateCount }: INewsListItem) => {
    const displayedRealestates = realestates.slice(0, DISPLAYED_REALESTATES_COUNT);

    return { displayedRealestates, moreRealestatesCount: realestateCount - displayedRealestates.length };
};
